import {
  QueryHookOptions,
  useApolloClient,
  useMutation,
  useQuery
} from '@apollo/client';
import { useAppDispatch } from 'src/app/hooks';
import {
  reducerUpdateGroupObject,
  reducerUpdateObjectList,
  reducerUpdateObjectListWithStatus,
  reducerUpdateObjectManagementLimit,
  reducerUpdateObjectManagementStreamPreview,
  reducerUpdateObjectManagementStreamPreviewLoading,
  reducerUpdateSocialProfileAutoComplete,
  reducerUpdateSocialProfileAutoCompleteLoading
} from 'src/features/objectManagement';
import {
  IGroupObject,
  IGroupObjectPayload,
  IObjectAutoCompletePayload,
  IObjectLimit,
  IObjectManagementStreamPreview,
  IObjectManagementStreamPreviewPayload,
  IObject,
  ISocialProfileAutoComplete
} from 'src/models/objectManagement';
import {
  OBJECT_MANAGEMENT_ADD_GROUP,
  OBJECT_MANAGEMENT_DELETE_GROUP,
  OBJECT_MANAGEMENT_EDIT_GROUP,
  OBJECT_MANAGEMENT_DELETE_OBJECT,
  OBJECT_MANAGEMENT_EDIT_OBJECT,
  OBJECT_MANAGEMENT_ADD_OBJECT,
  OBJECT_MANAGEMENT_INTEGRATE_OBJECT
} from './graphql/objectManagement/mutation';
import {
  GET_LAST_UPDATED_DATA,
  GET_OBJECT_LIMIT,
  GET_OBJECTS_WITH_STATUS,
  OBJECT_MANAGEMENT_GET_AUTOCOMPLETE,
  OBJECT_MANAGEMENT_GET_GROUP_LIST,
  OBJECT_MANAGEMENT_GET_STREAM_PREVIEW
} from './graphql/objectManagement/query';
import { API_ONBOARDING_UPDATE_ROLE_AND_INDUSTRY } from './graphql/onboarding/mutation';
import { useAlert } from 'src/hooks/useAlert';
import { ECOMMERCE_SOURCES } from 'src/constants';
import { TEcommerce } from 'src/components/EcommerceObjectPreview';

interface IGetObjectAutoCompleteResponse {
  objectManagement_getAutocomplete: Array<ISocialProfileAutoComplete>;
}

interface IGetObjectListResponse {
  objectManagement_getObjectList: Array<IObject>;
}

export interface IGetGroupObjectResponse {
  objectManagement_getObjectGroupList: Array<IGroupObject>;
}

interface IGetObjectManagementStreamPreviewResponse {
  objectManagement_getStreamPreview: IObjectManagementStreamPreview;
}

interface IGetObjectLimitResponse {
  objectManagement_getObjectLimit: IObjectLimit;
}

export interface IGetObjectsWithEntitiesResponse {
  objectManagement_getObjectListIncludeEntity: IObject[];
}

export interface IGetObjectsWithStatusResponse {
  objectManagement_getObjectListWithStatus: IObject[];
}

export interface IIntegrateTiktokObjectResponse {
  objectManagement_unauthorizeObject: IObject;
}

export interface IIntegrateTiktokObjectPayload {
  projectId: string;
  objectId: string;
}

const useObjectManagement = function () {
  const client = useApolloClient();
  const dispatch = useAppDispatch();
  const { handleClickAlert } = useAlert();

  const fetchObjectManagementAutoComplete = async function (
    payload: IObjectAutoCompletePayload
  ) {
    return await client
      .query<IGetObjectAutoCompleteResponse>({
        query: OBJECT_MANAGEMENT_GET_AUTOCOMPLETE,
        variables: payload
      })
      .then((response) => {
        return {
          ...response,
          data: response.data?.objectManagement_getAutocomplete
        };
      });
  };

  const getObjectManagementAutoComplete = async function (
    payload: IObjectAutoCompletePayload
  ) {
    dispatch(reducerUpdateSocialProfileAutoCompleteLoading(true));
    const response = await fetchObjectManagementAutoComplete(payload);
    dispatch(reducerUpdateSocialProfileAutoCompleteLoading(false));
    if (!response.data) return;
    dispatch(reducerUpdateSocialProfileAutoComplete(response));
  };

  const fetchObjectList = async function (projectId: string) {
    return await client
      .query<IGetObjectsWithStatusResponse>({
        query: GET_OBJECTS_WITH_STATUS,
        variables: { projectId }
      })
      .then((response) => {
        return {
          ...response,
          data: response.data.objectManagement_getObjectListWithStatus
        };
      });
  };

  const getObjectList = async function (payload: string) {
    const response = await fetchObjectList(payload);
    if (!response.data) return;
    dispatch(
      reducerUpdateObjectList({
        ...response,
        data: response.data.filter(
          (object) =>
            !ECOMMERCE_SOURCES.includes(object.socialMedia as TEcommerce)
        )
      })
    );
    return response;
  };

  const fetchGroupObject = async function (payload: IGroupObjectPayload) {
    return await client
      .query<IGetGroupObjectResponse>({
        query: OBJECT_MANAGEMENT_GET_GROUP_LIST,
        variables: { ...payload, objectGroupType: 'socmed' }
      })
      .then((response) => {
        return {
          ...response,
          data: response.data.objectManagement_getObjectGroupList
        };
      });
  };

  const getGroupObject = async function (payload: IGroupObjectPayload) {
    const response = await fetchGroupObject(payload);
    if (!response.data) return;
    dispatch(reducerUpdateGroupObject(response));
    return response;
  };

  const fetchStreamPreview = async function (
    payload: IObjectManagementStreamPreviewPayload
  ) {
    return await client
      .query<IGetObjectManagementStreamPreviewResponse>({
        query: OBJECT_MANAGEMENT_GET_STREAM_PREVIEW,
        variables: payload
      })
      .then((response) => {
        return {
          ...response,
          data: response.data.objectManagement_getStreamPreview
        };
      });
  };

  const getObjectManagementStreamPreview = async function (
    payload: IObjectManagementStreamPreviewPayload
  ) {
    dispatch(reducerUpdateObjectManagementStreamPreviewLoading(true));
    const response = await fetchStreamPreview(payload);
    if (!response.data) return;
    dispatch(reducerUpdateObjectManagementStreamPreview(response));
  };

  const fetchObjectLimit = async function (projectId: string) {
    return await client
      .query<IGetObjectLimitResponse>({
        query: GET_OBJECT_LIMIT,
        variables: { projectId }
      })
      .then((response) => {
        return {
          ...response,
          data: response.data.objectManagement_getObjectLimit
        };
      });
  };

  const getObjectObjectLimit = async function (projectId: string) {
    const response = await fetchObjectLimit(projectId);
    if (!response.data) return;
    dispatch(reducerUpdateObjectManagementLimit(response));
  };

  const [updateRoleAndIndustry, responseUpdateRoleAndIndustry] = useMutation(
    API_ONBOARDING_UPDATE_ROLE_AND_INDUSTRY
  );
  const [objectManagementAddGroup, resObjectManagementAddGroup] = useMutation(
    OBJECT_MANAGEMENT_ADD_GROUP
  );
  const [objectManagementEditGroup, resObjectManagementEditGroup] = useMutation(
    OBJECT_MANAGEMENT_EDIT_GROUP
  );
  const [objectManagementDeleteGroup, resObjectManagementDeleteGroup] =
    useMutation(OBJECT_MANAGEMENT_DELETE_GROUP);

  const [postAddObject, responseAddObject] = useMutation(
    OBJECT_MANAGEMENT_ADD_OBJECT
  );

  const [postEditObject, responseEditObject] = useMutation(
    OBJECT_MANAGEMENT_EDIT_OBJECT
  );

  const [postDeleteObject, resDeleteObject] = useMutation(
    OBJECT_MANAGEMENT_DELETE_OBJECT,
    {
      onError() {
        handleClickAlert({
          message: 'Error deleting alert, please try again.',
          severity: 'error'
        });
      },
      onCompleted() {
        handleClickAlert({
          message: 'Object deleted successfully.',
          severity: 'success'
        });
      }
    }
  );

  const [disconnectTiktokObject, resDisconnectTiktokObject] = useMutation<
    IIntegrateTiktokObjectResponse,
    IIntegrateTiktokObjectPayload
  >(OBJECT_MANAGEMENT_INTEGRATE_OBJECT);

  const useGetObjectListWithStatus = (
    options: QueryHookOptions<
      IGetObjectsWithStatusResponse,
      { projectId: string }
    >
  ) => {
    return useQuery<IGetObjectsWithStatusResponse, { projectId: string }>(
      GET_OBJECTS_WITH_STATUS,
      {
        ...options,
        onCompleted: (response) => {
          dispatch(
            reducerUpdateObjectListWithStatus(
              response.objectManagement_getObjectListWithStatus.filter(
                (object) =>
                  !ECOMMERCE_SOURCES.includes(object.socialMedia as TEcommerce)
              )
            )
          );
        }
      }
    );
  };

  const useGetLastUpdatedData = (
    options: QueryHookOptions<
      { objectManagement_getLastDataUpdate: Date },
      { projectId: string }
    >
  ) => {
    return useQuery<
      { objectManagement_getLastDataUpdate: Date },
      { projectId: string }
    >(GET_LAST_UPDATED_DATA, {
      ...options
    });
  };

  return {
    updateRoleAndIndustry,
    responseUpdateRoleAndIndustry,
    getObjectManagementAutoComplete,
    getObjectList,
    getGroupObject,
    getObjectManagementStreamPreview,
    objectManagementAddGroup,
    resObjectManagementAddGroup,
    objectManagementEditGroup,
    resObjectManagementEditGroup,
    objectManagementDeleteGroup,
    resObjectManagementDeleteGroup,
    postDeleteObject,
    resDeleteObject,
    postEditObject,
    responseEditObject,
    getObjectObjectLimit,
    postAddObject,
    responseAddObject,
    disconnectTiktokObject,
    resDisconnectTiktokObject,
    useGetObjectListWithStatus,
    useGetLastUpdatedData
  };
};

export default useObjectManagement;
